import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  IconButton, Button, Box, Tooltip, Dialog, DialogTitle, DialogContent,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import NovoAcordo from './NovoAcordo';

const FASE_JURIDICO = 'Jurídico';

const ButtonAcordo = styled(Button, {
  shouldForwardProp: prop => prop !== 'ativo',
})(({ ativo }) => ({
  height: 35,
  backgroundColor: ativo ? 'transparent' : '#423362',
  color: ativo ? '#423362' : '#ffffff',
  border: ativo ? '2px solid #423362' : 'none',
  '&:hover': {
    backgroundColor: ativo ? 'transparent' : '#423362',
    opacity: 0.64,
  },
  '&.Mui-disabled': {
    backgroundColor: '#c4c4c4',
  },
}));

/**
 * Port 1:1 da lógica de habilitação/toggle de `ButtonNovoAcordo`/
 * `onClickButtonNovoAcordo` (tela real, `src/pages/InspecionarProcesso/Acordos/Acordos.jsx`).
 *
 * Estado "aberto" persistido por processo via `useRef` (mesma técnica de
 * `novoAcordoStateByProcess` real): trocar de processo e voltar preserva se o
 * painel estava aberto para aquele processo especificamente.
 *
 * O fluxo (Passo 1/2/3, `NovoAcordo`) abre num `Dialog` modal (AW2-1032 Etapa 9,
 * decisão revista após a implementação inicial em card expandido) — fechamento
 * acidental (Esc/clique fora) é bloqueado de propósito, pois o fluxo termina
 * numa mutação real no backend (gerar acordo): `disableEscapeKeyDown` neutraliza
 * o Esc, e `onClose` ignora `reason === 'backdropClick'`. Fechar só é possível
 * via ação explícita — o ícone "X" abaixo, "Voltar" do Passo 1, ou sucesso ao
 * gerar o acordo no Passo 3 (ambos já chamam `fechar`/`onFechar`, sem mudança).
 *
 * Achado em code review: com `NovoAcordo` agora dentro de um `Dialog`, todo
 * sub-dialog do fluxo (`DialogInstrucaoInicial`, `DialogCampanha`,
 * `DialogMemoriaDeCalculo`, `DialogAlterarParcela`, e os 2 dialogs inline de
 * `NovoAcordoPassoTres.jsx`) empilharia um 2º backdrop por cima deste,
 * escurecendo mais do que o esperado — cada um desses recebeu `hideBackdrop`
 * (o backdrop deste `Dialog` externo já é suficiente). Qualquer novo dialog
 * adicionado dentro do fluxo Novo Acordo precisa do mesmo `hideBackdrop`.
 */
export default function BotaoNovoAcordo({
  idProcesso, podeModificarFase, processoComTitulosLiberados, nomeUsuario, nomeFase, cobrancaJudicial, onAcordoGerado,
}) {
  const abertoPorProcesso = useRef({});
  const [aberto, setAberto] = useState(() => abertoPorProcesso.current[idProcesso] ?? false);

  useEffect(() => {
    setAberto(abertoPorProcesso.current[idProcesso] ?? false);
  }, [idProcesso]);

  useEffect(() => {
    if (cobrancaJudicial) {
      abertoPorProcesso.current[idProcesso] = false;
      setAberto(false);
    }
  }, [cobrancaJudicial, idProcesso]);

  const desabilitado = !podeModificarFase
    || processoComTitulosLiberados === false
    || (nomeUsuario === null && nomeFase !== FASE_JURIDICO);

  const onClick = () => {
    setAberto((atual) => {
      const novoValor = !atual;
      abertoPorProcesso.current[idProcesso] = novoValor;
      return novoValor;
    });
  };

  const fechar = () => {
    abertoPorProcesso.current[idProcesso] = false;
    setAberto(false);
  };

  const onCloseDialog = (event, reason) => {
    if (reason !== 'backdropClick') {
      fechar();
    }
  };

  return (
    <Box>
      <Tooltip title="Novo Acordo ou Simulação">
        <span>
          <ButtonAcordo ativo={aberto} disabled={desabilitado} onClick={onClick}>
            ACORDO
          </ButtonAcordo>
        </span>
      </Tooltip>
      <Dialog
        open={aberto}
        onClose={onCloseDialog}
        disableEscapeKeyDown
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
        >
          Novo Acordo ou Simulação
          <Tooltip title="Fechar">
            <IconButton onClick={fechar} aria-label="Fechar" size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent dividers>
          {aberto && (
            <NovoAcordo idProcesso={idProcesso} onFechar={fechar} onAcordoGerado={onAcordoGerado} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

BotaoNovoAcordo.propTypes = {
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  podeModificarFase: PropTypes.bool,
  processoComTitulosLiberados: PropTypes.bool,
  nomeUsuario: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  nomeFase: PropTypes.string,
  cobrancaJudicial: PropTypes.bool,
  onAcordoGerado: PropTypes.func,
};

BotaoNovoAcordo.defaultProps = {
  idProcesso: null,
  podeModificarFase: false,
  processoComTitulosLiberados: null,
  nomeUsuario: null,
  nomeFase: null,
  cobrancaJudicial: false,
  onAcordoGerado: null,
};
